import { z } from 'zod';
import type { SystemEntity } from '@erp/shared-types';
import type { ListTasksInput } from '@erp/rpc';
import {
  TASKS_QUEUE_DEFAULT_PAGE_SIZE,
  buildTaskListInput,
  tasksQueueSearchSchema,
  type TasksQueueSearch,
} from './taskQueueSearch';

/**
 * Search params for `/tasks/$taskId`.
 *
 * `fromPage` rebuilds breadcrumbs back to a task page. `chat=open` is the
 * deep link Nami's follow-up notifications use. `list=queue` plus the queue's
 * own filters means the task was opened from a task list, so previous/next
 * can walk that same filtered list.
 */
export const taskDetailSearchSchema = tasksQueueSearchSchema.extend({
  fromPage: z.uuid().optional(),
  chat: z.literal('open').optional(),
  list: z.literal('queue').optional(),
});

export type TaskDetailSearch = z.infer<typeof taskDetailSearchSchema>;

/**
 * Search params to attach when linking to `/tasks/$taskId`.
 * Pass `queue` when the link is a row in a task list so the detail page can
 * move to the next task in that list.
 */
export function taskDetailSearch(
  fromPage: string | undefined,
  queue?: TasksQueueSearch,
): TaskDetailSearch | undefined {
  if (!fromPage && !queue) return undefined;
  return {
    ...(fromPage ? { fromPage } : {}),
    ...(queue ? { list: 'queue' as const, ...compactQueueSearch(queue) } : {}),
  };
}

/**
 * The queue filters a detail URL is walking, or null when the task was not
 * opened from a task list.
 */
export function queueSearchFromTaskDetail(
  search: TaskDetailSearch,
): TasksQueueSearch | null {
  if (search.list !== 'queue') return null;
  return compactQueueSearch(search);
}

/**
 * Task pages pin the type outside `?type=`. Detail navigation still needs the
 * key so it lists the same tasks the page does.
 */
export function queueSearchForDetail(
  search: TasksQueueSearch,
  pinnedTypeKey: string | undefined,
): TasksQueueSearch {
  if (search.type || !pinnedTypeKey) return search;
  return { ...search, type: pinnedTypeKey };
}

/** Drop empty filters so the detail URL stays readable. Page 1 is the default. */
export function compactQueueSearch(search: TasksQueueSearch): TasksQueueSearch {
  const compact: TasksQueueSearch = {};
  if (search.type) compact.type = search.type;
  if (search.status && search.status.length > 0) compact.status = search.status;
  if (search.assignee) compact.assignee = search.assignee;
  if (search.outcome) compact.outcome = search.outcome;
  const trimmedSearch = search.search?.trim();
  if (trimmedSearch) compact.search = trimmedSearch;
  if (search.page && search.page > 1) compact.page = search.page;
  if (search.pageSize) compact.pageSize = search.pageSize;
  if (search.sortBy) compact.sortBy = search.sortBy;
  if (search.sortOrder) compact.sortOrder = search.sortOrder;
  if (search.showSnoozed !== undefined)
    compact.showSnoozed = search.showSnoozed;
  if (search.ref && Object.keys(search.ref).length > 0)
    compact.ref = search.ref;
  return compact;
}

/** Keep the list context and move to the neighbor's page. Drops `chat=open`. */
export function taskNavigationSearch(
  current: TaskDetailSearch,
  page: number,
): TaskDetailSearch {
  return {
    ...current,
    list: 'queue',
    page: page > 1 ? page : undefined,
    chat: undefined,
  };
}

/**
 * True when the detail page has everything it needs to reproduce the queue
 * query. 'mine' waits for the signed-in user; a type key waits for the registry.
 */
export function taskQueueQueryReady(args: {
  queue: TasksQueueSearch | null;
  typeId: string | undefined;
  currentUserId: string | undefined;
  typesLoaded: boolean;
}): boolean {
  if (!args.queue) return false;
  if (args.queue.assignee === 'mine' && !args.currentUserId) return false;
  if (!args.queue.type) return true;
  return args.typesLoaded && args.typeId !== undefined;
}

type TaskTypeRef = {
  id: string;
  key: string;
  anchorEntityType?: SystemEntity | null;
};

export function taskTypeIdForKey(
  types: ReadonlyArray<TaskTypeRef> | undefined,
  key: string | undefined,
): string | undefined {
  return taskTypeForKey(types, key)?.id;
}

function taskTypeForKey(
  types: ReadonlyArray<TaskTypeRef> | undefined,
  key: string | undefined,
): TaskTypeRef | undefined {
  if (!key || !types) return undefined;
  return types.find((type) => type.key === key);
}

export type TaskListQueryPlan = {
  ready: boolean;
  page: number;
  pageSize: number;
  current: ListTasksInput;
  previous: ListTasksInput;
  next: ListTasksInput;
};

/**
 * The three `tasks.list` queries previous/next needs: the page the detail URL
 * is on, plus the pages on either side. Not ready until a 'mine' filter has a
 * user and a type key has resolved.
 */
export function taskListQueryPlan(args: {
  search: TaskDetailSearch;
  types: ReadonlyArray<TaskTypeRef> | undefined;
  typesLoaded: boolean;
  currentUserId: string | undefined;
}): TaskListQueryPlan {
  const queue = queueSearchFromTaskDetail(args.search);
  const type = taskTypeForKey(args.types, queue?.type);
  const typeId = type?.id;
  const ready = taskQueueQueryReady({
    queue,
    typeId,
    currentUserId: args.currentUserId,
    typesLoaded: args.typesLoaded,
  });
  const filters = queue ?? {};
  const page = listPage(filters);
  const opts = {
    currentUserId: args.currentUserId,
    taskTypeId: typeId,
    anchorEntityType: type?.anchorEntityType,
  };
  const current = buildTaskListInput({ ...filters, page }, opts);
  return {
    ready,
    page,
    pageSize: current.limit ?? TASKS_QUEUE_DEFAULT_PAGE_SIZE,
    current,
    previous: buildTaskListInput(
      { ...filters, page: Math.max(1, page - 1) },
      opts,
    ),
    next: buildTaskListInput({ ...filters, page: page + 1 }, opts),
  };
}

function listPage(search: TasksQueueSearch): number {
  return search.page && search.page > 0 ? search.page : 1;
}
